import React, { useCallback, useEffect, useMemo, useState } from 'react'
import HeaderTitle from '_/components/header-title'
import { Grid, Button, Typography } from '@mui/material'
import { navigate, useParams } from '@reach/router'
import { ROUTES } from '_/utils/constants'
import { Button as ButtonRefera, Loader } from '@refera/ui-web'
import Theme from '@refera/ui-core'
import humps from 'humps'

import { FilterSearch as FilterIcon } from '@refera/ui-icons'

import useStyles from './styles'
import { useDispatch } from 'react-redux'
import { getReportById, getReportsByFilters, getReportsToDownload } from '_/modules/reports/actions'
import { useForm } from 'react-hook-form'
import { useToast } from '_/hooks/use-toast'
import { FiltersReport } from './filters'
import ReportDataTable from './data-table'
import moment from 'moment'
import { useDialog } from '_/hooks/use-dialog'
import { exportToXLSX } from '_/utils/export-to-excel'

export default function ReportDetail() {
  const styles = useStyles()
  const dispatch = useDispatch()

  const { showDialog, closeDialog } = useDialog()

  const [isLoading, setIsLoading] = useState(false)
  const [dataReport, setDataReport] = useState({})

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(true)
  const { showToast } = useToast()
  const { reportId } = useParams()
  const [report, setReport] = useState({
    title: reportId,
  })
  const [filtersField, setFiltersField] = useState([])

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      title: '',
    },
  })

  const { setValue } = methods

  const getReport = useCallback(() => {
    if (reportId) {
      setIsLoading(true)

      return dispatch(getReportById(reportId))
        .then(data => {
          setFiltersField(data?.filters ?? [])
          setReport({
            title: data?.title,
            description: data?.description,
            id: data?.id,
          })
          setValue('title', data?.title)
        })
        .catch(() => {
          showToast({ type: 'error', message: 'Ocorreu um erro ao buscar dados do relatório.' })
          return setIsLoading(false)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
    return null
  }, [dispatch, reportId])

  useEffect(() => {
    getReport()
  }, [reportId])

  const goBack = () => {
    navigate(`${ROUTES.REPORTS}`)
  }

  const handleGetDataToShow = useCallback((filterParams = null) => {
    if (reportId && filterParams) {
      setIsLoading(true)

      return dispatch(
        getReportsByFilters(reportId, {
          filterParams,
        })
      )
        .then(dataResponse => {
          const columns = dataResponse?.columns
            .map(item => {
              const camel = humps.camelize(item?.name)
              const field = String(camel)?.toLocaleLowerCase()
              return {
                field: field === 'id' ? 'key' : field,
                headerName: item?.name,
                width: item?.name.includes('Prioridade') ? 180 : item?.width ?? '100%',
                editable: false,
              }
            })
            ?.filter(item => item?.field !== 'link')

          const results = dataResponse?.results?.map((row, index) => {
            const formatted = Object.fromEntries(
              Object.entries(row).map(([key, val]) => {
                const isDate =
                  val instanceof Date ||
                  (typeof val === 'string' && moment(val, moment.ISO_8601, true).isValid())

                return [
                  String(key)?.toLocaleLowerCase(),
                  isDate ? moment(val).format('DD/MM/YYYY') : val,
                ]
              })
            )

            return {
              ...formatted,
              key: row.id ?? index,
              id: index,
            }
          })
          setDataReport({ ...dataResponse, results, columns })
        })
        .catch(() => {
          showToast({
            type: 'error',
            message: 'Ocorreu um erro ao buscar dados com esse filtro.',
          })
          return setIsLoading(false)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
    showToast({ type: 'error', message: 'Ocorreu um erro ao buscar dados.' })

    return null
  }, [])

  const handleExport = useCallback(async () => {
    if (reportId) {
      try {
        setIsLoading(true)
        const filterParams = dataReport?.results?.map(item => item.key)

        const response = await getReportsToDownload(reportId, {
          serviceOrders: filterParams,
        })

        const now = moment().format('DD-MM-YY_HH-mm')
        const fileName = `${report?.title}_${now}`

        if (!response?.data) {
          return showToast({
            type: 'error',
            message: 'Não há nenhum registro para exportar',
          })
        }

        exportToXLSX(response?.data, fileName)
        return setIsLoading(false)
      } catch (error) {
        console.error({ error })
        setIsLoading(false)
        return showToast({
          type: 'error',
          message: 'Não foi possível executar esta ação, tente novamente.',
        })
      }
    }

    return showToast({
      type: 'error',
      message: 'Não foi possível executar esta ação, tente novamente.',
    })
  }, [dataReport?.results, reportId])

  const handleExportData = useCallback(() => {
    if (!dataReport?.results || dataReport?.results?.length === 0) {
      return showToast({
        type: 'error',
        message: 'Não há nenhum registro para exportar',
      })
    }

    showDialog({
      type: 'warning',
      subject: 'Você tem certeza que deseja executar esta ação?',
      onApprove: () => {
        handleExport()
        closeDialog()
      },
      labelApprove: 'Sim',
      labelCancel: 'Não',
      onCancel: () => {
        closeDialog()
      },
    })

    return null
  }, [dataReport?.results, reportId])

  const renderFilters = useMemo(
    () => (
      <FiltersReport
        open={filterDrawerOpen}
        close={() => setFilterDrawerOpen(false)}
        applyFilters={handleGetDataToShow}
        filtersField={filtersField}
      />
    ),
    [filterDrawerOpen, handleGetDataToShow, isLoading]
  )

  return (
    <Grid className={styles.app}>
      {renderFilters}
      <HeaderTitle title={`Relatório ${report?.title}`} backButtonAction={goBack}>
        <Grid style={{ display: 'flex', gap: '8px' }}>
          <Button className={[styles.buttonBlue, styles.button]} onClick={() => handleExportData()}>
            Exportar
          </Button>
          {/* <Button className={[styles.buttonBlue, styles.button]} onClick={() => {}}>
            Imprimir
          </Button> */}

          <ButtonRefera
            disabled={isLoading}
            variant="ghost"
            startIcon={<FilterIcon color={Theme.Colors.Primary.Base} />}
            onClick={() => setFilterDrawerOpen(true)}
          >
            Filtros
          </ButtonRefera>
        </Grid>
      </HeaderTitle>

      <Grid className={styles.container}>
        {report?.description && (
          <Grid className={styles.description}>
            <Typography className={styles.title}>{report?.description}</Typography>
          </Grid>
        )}
        {/* <Grid className={styles.containerView}>
          <Typography className={styles.text}>Opções de visualização</Typography>

          <Grid className={styles.flex}>
            <Select
              label="Ordenado por"
              options={[
                { label: 'Nome', value: 'name' },
                { label: 'Data', value: 'date' },
                { label: 'Valor', value: 'value' },
              ]}
              value={orderBy}
              onChange={event => {
                const { value } = event.target
                setOrderBy(value)
              }}
              className={styles.select}
            />
          </Grid>
        </Grid> */}
        <Grid className={styles.tableContainer}>
          {/* <Typography className={styles.title}>{report?.title}</Typography> */}
          <ReportDataTable
            dataToShow={dataReport.results ?? []}
            count={dataReport.count ?? 0}
            columns={
              dataReport.columns ?? [
                { field: 'key', headerName: 'id', width: 100, editable: false },
              ]
            }
          />
        </Grid>
      </Grid>

      <Loader hasBackdrop open={isLoading} label="Aguarde..." />
    </Grid>
  )
}
